import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { MarketingPage, isMarketingPath } from "./marketing";
import { navigate } from "./navigation";
import { LinkButton, Spinner } from "./ui";

const AuthPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.AuthPage })),
);
const DealAccountGate = lazy(() =>
  import("./pages/auth").then((module) => ({
    default: module.DealAccountGate,
  })),
);
const PortalPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.PortalPage })),
);
const RequireAccount = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.RequireAccount })),
);
const CustomerDealPage = lazy(() =>
  import("./pages/customer").then((module) => ({
    default: module.CustomerDealPage,
  })),
);
const StartDealPage = lazy(() =>
  import("./pages/customer").then((module) => ({
    default: module.StartDealPage,
  })),
);
const StartRoute = lazy(() =>
  import("./public-qualifier").then((module) => ({
    default: module.StartRoute,
  })),
);
const OperatorHomePage = lazy(() =>
  import("./pages/operator").then((module) => ({
    default: module.OperatorHomePage,
  })),
);
const OperatorWorkspacePage = lazy(() =>
  import("./pages/operator").then((module) => ({
    default: module.OperatorWorkspacePage,
  })),
);
const ResearchPage = lazy(() =>
  import("./pages/research").then((module) => ({
    default: module.ResearchPage,
  })),
);

function DeferredPage({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<Spinner label="Opening your secure lane…" />}>
      {children}
    </Suspense>
  );
}

function usePath() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    const onDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a");
      if (!anchor || anchor.target || anchor.hasAttribute("download")) return;

      const url = new URL(anchor.href, window.location.href);
      if (
        url.origin !== window.location.origin ||
        url.pathname === window.location.pathname ||
        isMarketingPath(window.location.pathname) ||
        isMarketingPath(url.pathname)
      ) {
        return;
      }

      event.preventDefault();
      navigate(`${url.pathname}${url.search}${url.hash}`);
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onDocumentClick);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onDocumentClick);
    };
  }, []);

  return path.replace(/\/+$/, "") || "/";
}

function PrivateNotFoundPage() {
  return (
    <div className="buyer-theme error-state">
      <p className="eyebrow">404</p>
      <h1>That road ends here.</h1>
      <p>The page may have moved, or the private link is incomplete.</p>
      <LinkButton href="/">Return home</LinkButton>
    </div>
  );
}

export function App() {
  const path = usePath();

  if (isMarketingPath(path)) return <MarketingPage pathname={path} />;
  if (path === "/login") {
    return <DeferredPage><AuthPage mode="login" /></DeferredPage>;
  }
  if (path === "/signup") {
    return <DeferredPage><AuthPage mode="signup" /></DeferredPage>;
  }
  if (path === "/forgot-password") {
    return <DeferredPage><AuthPage mode="forgot" /></DeferredPage>;
  }
  if (path === "/reset-password") {
    return <DeferredPage><AuthPage mode="reset" /></DeferredPage>;
  }
  if (path === "/portal") {
    return <DeferredPage><PortalPage /></DeferredPage>;
  }
  if (path === "/start") {
    return (
      <DeferredPage>
        <StartRoute>
          <StartDealPage />
        </StartRoute>
      </DeferredPage>
    );
  }
  if (path === "/operator") {
    return (
      <DeferredPage>
        <RequireAccount next="/operator" role="operator">
          <OperatorHomePage />
        </RequireAccount>
      </DeferredPage>
    );
  }
  if (path === "/operator/research") {
    return (
      <DeferredPage>
        <RequireAccount next="/operator/research" role="operator">
          <ResearchPage />
        </RequireAccount>
      </DeferredPage>
    );
  }

  const mandateRevisionToken = path.match(/^\/deal\/([^/]+)\/revise$/u)?.[1];
  if (mandateRevisionToken) {
    return (
      <DeferredPage>
        <DealAccountGate token={mandateRevisionToken}>
          <StartDealPage revisionToken={mandateRevisionToken} />
        </DealAccountGate>
      </DeferredPage>
    );
  }

  const dealToken = path.match(/^\/deal\/([^/]+)$/u)?.[1];
  if (dealToken) {
    return (
      <DeferredPage>
        <DealAccountGate token={dealToken}>
          <CustomerDealPage token={dealToken} />
        </DealAccountGate>
      </DeferredPage>
    );
  }

  const operatorAuditDealId = path.match(
    /^\/operator\/deals\/([^/]+)\/audit$/u,
  )?.[1];
  if (operatorAuditDealId) {
    return (
      <DeferredPage>
        <RequireAccount
          next={`/operator/deals/${operatorAuditDealId}/audit`}
          role="operator"
        >
          <OperatorWorkspacePage dealId={operatorAuditDealId} view="audit" />
        </RequireAccount>
      </DeferredPage>
    );
  }

  const operatorDealId = path.match(/^\/operator\/deals\/([^/]+)$/u)?.[1];
  if (operatorDealId) {
    return (
      <DeferredPage>
        <RequireAccount next={`/operator/deals/${operatorDealId}`} role="operator">
          <OperatorWorkspacePage dealId={operatorDealId} />
        </RequireAccount>
      </DeferredPage>
    );
  }

  return <PrivateNotFoundPage />;
}
